import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { toaster } from '../utils/toast';
import {
  LIABILITY_FILES_KEY,
  LIABILITY_FILES_KEY_MAP,
  LIABILITY_IMAGE_FILES_KEY,
  LIABILITY_TEXT_FIELDS_KEY_MAP,
  LIABILITY_IMAGE_FILES_KEY_MAP,
  REQUEST_TYPES,
  SW_ANALYSIS_TYPE,
  SW_ANALYSIS_TYPES_REQUIRING_OCCUPANTS,
  SW_OCCUPANT_POSITION,
  GENDER,
  SW_AIRBAG_DEPLOYED
} from '@demandproai/shared';
import Constants from '../Constants';
import usePfqAxios from './usePfqAxios';
import useCaseData from './useCaseData';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import { normalizeGender } from '../utils/genderNormalization';

const PERSONAL_INFO_GENDER_BY_SW = {
  [GENDER.MALE]: 'Male',
  [GENDER.FEMALE]: 'Female'
};

const denormalizeGenderForPersonalInfo = (swGender) => PERSONAL_INFO_GENDER_BY_SW[swGender] || '';

const REQUIRED_LIABILITY_GROUPS = [
  {
    key: LIABILITY_FILES_KEY.POLICE_REPORT_DATA,
    label: LIABILITY_FILES_KEY_MAP[LIABILITY_FILES_KEY.POLICE_REPORT_DATA].text
  },
  {
    key: LIABILITY_IMAGE_FILES_KEY.ACCIDENT_SCENE_FILES,
    label: LIABILITY_IMAGE_FILES_KEY_MAP[LIABILITY_IMAGE_FILES_KEY.ACCIDENT_SCENE_FILES].text
  }
];

const DEFAULT_OCCUPANT_INPUT = {
  age: '',
  gender: '',
  heightFeet: '',
  heightInchesPart: '',
  weightLbs: '',
  position: SW_OCCUPANT_POSITION.DRIVER,
  seatbeltWorn: true,
  airbagDeployed: SW_AIRBAG_DEPLOYED.UNKNOWN
};

const toTotalInches = ({ heightFeet, heightInchesPart }) => {
  const feet = Number(heightFeet) || 0;
  const inches = Number(heightInchesPart) || 0;
  return Math.round(feet * 12 + inches);
};

const useSilentWitness = ({ onClose } = {}) => {
  const { caseId } = useParams();
  const { formik } = useAddCaseFormContext();
  const [selectedLiabilityItems, setSelectedLiabilityItems] = useState({});
  const [analysisType, setAnalysisType] = useState(SW_ANALYSIS_TYPE.ACCIDENT_ONLY);
  const [occupantInputs, setOccupantInputs] = useState({});
  const { postData: generateSilentWitnessReportPostData, isLoading: generateSilentWitnessReportIsLoading } = usePfqAxios();
  const { updateCase, debounceUpdateCase } = useCaseFieldUpdate();
  const { postData: removeSilentWitnessReportPostData, isLoading: removeSilentWitnessReportIsLoading } = usePfqAxios();
  const { fetchCaseData } = useCaseData({ caseId });
  const { setFieldValue } = formik;

  // refresh formik with the latest server-side SW state when the modal opens (provider mounts);
  // the SW report is $push'd into liability.silentWitness by PFQ, so without this the modal sees stale data
  useEffect(() => {
    let isCurrent = true;
    const refresh = async () => {
      const freshCaseData = await fetchCaseData();
      if (!isCurrent || !freshCaseData) return;
      setFieldValue('liability.silentWitness', freshCaseData.liability?.silentWitness || []);
      setFieldValue('docxFiles.silent_witness.s3UrlPath', freshCaseData.docxFiles?.silent_witness?.s3UrlPath ?? null);
    };
    refresh();
    return () => { isCurrent = false; };
  }, [fetchCaseData, setFieldValue]);

  // latest SW report on the case (last $push'd entry)
  const latestSilentWitnessReportId = useMemo(() => {
    const reports = formik.values.liability?.silentWitness;
    if (!Array.isArray(reports) || reports.length === 0) return;
    return reports[reports.length - 1]?.reportId;
  }, [formik.values.liability]);

  const closeSilentWitnessModal = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // plaintiff clients surfaced to the occupants section — one entry per case client
  const plaintiffOccupantRows = useMemo(() => {
    const clients = formik.values.clients || [];
    return clients.map((client, index) => {
      const personalInfo = client.personalInfo || {};
      // seed per-client defaults from the client record so user-edited fields can override them
      const defaultInput = {
        ...DEFAULT_OCCUPANT_INPUT,
        age: personalInfo.age || '',
        gender: normalizeGender(personalInfo.gender) || ''
      };
      const input = { ...defaultInput, ...(occupantInputs[index] || {}) };
      return {
        index,
        clientName: personalInfo.clientName,
        age: input.age,
        gender: input.gender,
        genderRaw: personalInfo.gender,
        heightFeet: input.heightFeet,
        heightInchesPart: input.heightInchesPart,
        weightLbs: input.weightLbs,
        position: input.position,
        seatbeltWorn: input.seatbeltWorn,
        airbagDeployed: input.airbagDeployed
      };
    });
  }, [formik.values.clients, occupantInputs]);

  // age/gender are persistent client fields shared with the Pain & Suffering form,
  // so we route them through Formik + updateCase instead of the local occupant-only state
  const updateOccupantInput = useCallback(({ clientIndex, field, value }) => {
    if (field === 'age') {
      const path = `clients.${clientIndex}.personalInfo.age`;
      formik.setFieldValue(path, value);
      debounceUpdateCase({ data: { [path]: value } });
      return;
    }

    if (field === 'gender') {
      const path = `clients.${clientIndex}.personalInfo.gender`;
      const personalInfoGender = denormalizeGenderForPersonalInfo(value);
      formik.setFieldValue(path, personalInfoGender);
      updateCase({ data: { [path]: personalInfoGender } });
      return;
    }

    setOccupantInputs((prev) => ({
      ...prev,
      [clientIndex]: { ...(prev[clientIndex] || {}), [field]: value }
    }));
  }, [formik, updateCase, debounceUpdateCase]);

  // produces { occupants, missingFields } for the submit payload; returns { occupants: [] } when no occupants are needed
  const buildOccupantsForSubmit = useCallback(() => {
    if (!SW_ANALYSIS_TYPES_REQUIRING_OCCUPANTS.has(analysisType)) {
      return { occupants: [], missingFields: [] };
    }

    const occupants = [];
    const missingFields = [];

    plaintiffOccupantRows.forEach((row) => {
      const heightInches = toTotalInches({ heightFeet: row.heightFeet, heightInchesPart: row.heightInchesPart });
      const weight = Math.round(Number(row.weightLbs));
      const missing = [];
      if (!row.clientName) missing.push('name');
      if (!(Number(row.age) > 0)) missing.push('age');
      if (!row.gender) missing.push('gender (must be male or female)');
      if (!(heightInches > 0)) missing.push('height (feet/inches)');
      if (!(weight > 0)) missing.push('weight (lbs)');
      if (!row.position) missing.push('position');
      if (typeof row.seatbeltWorn !== 'boolean') missing.push('seatbelt worn');

      if (missing.length > 0) {
        missingFields.push({ clientName: row.clientName || `Client ${row.index + 1}`, missing });
        return;
      }

      occupants.push({
        clientName: row.clientName,
        age: Number(row.age),
        gender: row.gender,
        heightInches,
        weightLbs: weight,
        position: row.position,
        seatbeltWorn: row.seatbeltWorn,
        airbagDeployed: row.airbagDeployed
      });
    });

    return { occupants, missingFields };
  }, [analysisType, plaintiffOccupantRows]);

  const toggleItemSelection = useCallback(({ itemKey, index, type }) => {
    setSelectedLiabilityItems(prev => {
      const key = `${itemKey}-${index}`;
      const current = prev[key] || { plaintiff: false, defendant: false };

      return {
        ...prev,
        [key]: {
          ...current,
          [type]: !current[type]
        }
      };
    });
  }, []);

  const getLiabilityGroups = useCallback(() => {
    const liability = formik.values.liability || {};
    const groups = [];

    // File-based liability items
    Object.values(LIABILITY_FILES_KEY_MAP).forEach(({ key, text }) => {
      if (liability[key]?.length > 0) {
        groups.push({
          title: text,
          key,
          type: 'file',
          items: liability[key].map((item, index) => ({
            index,
            name: item.name,
            data: item
          }))
        });
      }
    });

    // Text-based liability items
    Object.values(LIABILITY_TEXT_FIELDS_KEY_MAP).forEach(({ key, text }) => {
      if (liability[key]?.length > 0) {
        groups.push({
          title: text,
          key,
          type: 'text',
          items: liability[key].map((item, index) => ({
            index,
            name: `Defendant ${index + 1} - ${text}`,
            data: item
          }))
        });
      }
    });

    // Image-based liability items
    Object.values(LIABILITY_IMAGE_FILES_KEY_MAP).forEach(({ key, text }) => {
      if (liability[key]?.length > 0) {
        groups.push({
          title: text,
          key,
          type: 'image',
          items: liability[key].map((item, index) => ({
            index,
            name: item.name,
            data: item
          }))
        });
      }
    });

    return groups;
  }, [formik.values.liability]);

  const buildSelectedItemsPayload = useCallback(({ selectedItems, liability }) => {
    const groupedItems = {};

    Object.entries(selectedItems).forEach(([key, selection]) => {
      if (selection.plaintiff || selection.defendant) {
        const [itemKey, index] = key.split('-');
        const item = liability[itemKey]?.[parseInt(index)];

        if (item) {
          if (!groupedItems[itemKey]) {
            groupedItems[itemKey] = [];
          }

          const itemData = {
            plaintiff: selection.plaintiff,
            defendant: selection.defendant
          };

          if (item.name) {
            itemData.name = item.name;
          }
          if (item.s3UrlPath) {
            itemData.s3UrlPath = item.s3UrlPath;
          }

          if (item.text) {
            itemData.text = item.text;
          }

          groupedItems[itemKey].push(itemData);
        }
      }
    });

    return groupedItems;
  }, []);

  /**
   * Find required liability groups that are missing a Plaintiff or Defendant selection.
   *
   * @param {Object} args
   * @param {Array} args.groups - groups produced by getLiabilityGroups
   * @param {Object} args.selectedItems - current selection map keyed by `${groupKey}-${itemIndex}`
   * @returns {string[]} labels of required groups missing a selection (empty when valid)
   */
  const findRequiredGroupsMissingSelection = useCallback(({ groups, selectedItems }) => {
    return REQUIRED_LIABILITY_GROUPS
      .filter(({ key }) => {
        const group = groups.find((g) => g.key === key);
        if (!group || group.items.length === 0) return true;
        return !group.items.some((item) => {
          const selection = selectedItems[`${group.key}-${item.index}`];
          return selection?.plaintiff || selection?.defendant;
        });
      })
      .map(({ label }) => label);
  }, []);

  const handleSubmit = useCallback(async () => {
    const liability = formik.values.liability || {};
    const groups = getLiabilityGroups();

    const missingRequiredGroups = findRequiredGroupsMissingSelection({ groups, selectedItems: selectedLiabilityItems });
    if (missingRequiredGroups.length > 0) {
      toaster({
        message: `Select at least one Plaintiff or Defendant for: ${missingRequiredGroups.join(', ')}`,
        success: false
      });
      return;
    }

    const { occupants, missingFields } = buildOccupantsForSubmit();
    if (missingFields.length > 0) {
      const firstMissing = missingFields[0];
      toaster({
        message: `Missing occupant fields for ${firstMissing.clientName}: ${firstMissing.missing.join(', ')}`,
        success: false
      });
      return;
    }

    const groupedItems = buildSelectedItemsPayload({
      selectedItems: selectedLiabilityItems,
      liability
    });

    const selectedSilentWitnessLiability = { ...groupedItems, analysisType };
    if (occupants.length > 0) {
      selectedSilentWitnessLiability.occupants = occupants;
    }

    try {
      const payload = {
        caseId,
        selectedSilentWitnessLiability,
        requestType: REQUEST_TYPES.SILENT_WITNESS
      };

      const response = await generateSilentWitnessReportPostData(Constants.ApiUrl.createRequest, payload);
      const { success, message } = response
      toaster({ message, success });

      if (success) {
        closeSilentWitnessModal();
      }

    } catch (error) {
      console.error('Error generating Silent Witness report:', error);
      toaster({ message: 'Failed to generate Silent Witness report', success: false });
    }
  }, [caseId, selectedLiabilityItems, formik.values.liability, buildSelectedItemsPayload, closeSilentWitnessModal, generateSilentWitnessReportPostData, getLiabilityGroups, findRequiredGroupsMissingSelection, analysisType, buildOccupantsForSubmit]);

  const handleRemoveReport = useCallback(async () => {
    if (!latestSilentWitnessReportId) return;
    if (!window.confirm('Remove Silent Witness report from this case? This cannot be undone.')) return;

    try {
      const response = await removeSilentWitnessReportPostData(
        Constants.ApiUrl.silentWitness.deleteReport,
        { caseId, reportId: latestSilentWitnessReportId }
      );
      const { success, message, data } = response;
      toaster({ message, success });

      if (success) {
        // hydrate from server-authoritative state so latestSilentWitnessReportId re-derives and the footer button re-validates against the updated array
        formik.setFieldValue('liability.silentWitness', data?.silentWitness || []);
        formik.setFieldValue('docxFiles.silent_witness.s3UrlPath', data?.silentWitnessDocxS3UrlPath ?? null);
      }
    } catch (error) {
      console.error('Error removing Silent Witness report:', error);
      toaster({ message: 'Failed to remove Silent Witness report', success: false });
    }
  }, [caseId, latestSilentWitnessReportId, formik, removeSilentWitnessReportPostData]);

  return {
    closeSilentWitnessModal,
    selectedLiabilityItems,
    toggleItemSelection,
    getLiabilityGroups,
    handleSubmit,
    generateSilentWitnessReportIsLoading,
    analysisType,
    setAnalysisType,
    plaintiffOccupantRows,
    updateOccupantInput,
    latestSilentWitnessReportId,
    handleRemoveReport,
    removeSilentWitnessReportIsLoading
  };
};

export default useSilentWitness;